// "What you can book." — three equally-weighted categories in the YMC
// (yellow / magenta / cyan) pan-flag palette. Each card opens a modal with
// specific offerings + (for Live Events) an add-ons strip.
//
// Modal is a bottom sheet on mobile, centered dialog on desktop.
// Each sub-option's "Ask about this →" button scrolls to the #contact
// section with a hash containing the offering slug so the eventual quote
// form can pre-select a subject.

import { useEffect, useState } from "react";
import PanBar from "../components/PanBar";

// Image slots use `image: null` by default — drop a file into /public/packages/
// and update the slot when you have art to use. Fallback is a category-tinted
// card without a photo, same shape as before.
export const CATEGORIES = [
  {
    key: "live-events",
    icon: "🎪",
    title: "Live Events",
    blurb: "We come to your event. Guests gather. The room erupts.",
    accent: "var(--jop-gold)",
    tint:   "rgba(252,179,31,0.07)",
    tintSolid: "#1a1a0a",
    glow:   "rgba(252,179,31,0.35)",
    items: [
      {
        slug: "performance",
        title: "The Performance",
        blurb: "Dan at the griddle, your guests gathered close. Every pancake painted live on a big display so the whole room watches the art take shape — golden tickets, the flip, and a keepsake they'll never forget.",
        image: "/packages/performance.webp", imagePos: "center 40%", /* griddle + hands + display */
      },
      {
        slug: "poffridges-bar",
        title: "Poffridge's Bar",
        blurb: "Wella's poffertjes station. Tiny Dutch pancakes with fresh toppings, served warm and endless. The line never stops smiling. Book it standalone or pair it with the Performance for the full experience.",
        image: "/packages/poffridges-bar.webp", imagePos: "center 30%", /* frosting tops */
      },
      {
        slug: "virtual-events",
        title: "Virtual Events",
        blurb: "The full Performance, streamed to a remote audience with pro-quality production. Conferences, team builds, watch-along parties — your guests tune in from anywhere and still get the magic.",
        image: "/packages/virtual-events.webp", imagePos: "70% 30%", /* Dan + griddle + monitor */
      },
    ],
    addOns: [
      {
        slug: "flash-menu",
        title: "Flash Menu",
        blurb: "Curated pre-set designs, quick and crowd-friendly. Everyone walks away with art.",
        image: "/packages/flash-menu.webp", imagePos: "center 20%", /* character grid */
      },
      {
        slug: "preservation-kits",
        title: "Preservation Kits & Prints",
        blurb: "Take-home kits so guests can preserve their pancake, plus signed prints on-site.",
        image: "/packages/preservation-kits.webp", imagePos: "center 40%", /* kit spread */
      },
      {
        slug: "social-coverage",
        title: "Social Media Coverage",
        blurb: "Our camera team delivers a post-ready reel, tagged photos, and promo content.",
        image: "/packages/social-coverage.webp", imagePos: "center 25%", /* viral pancake */
      },
      {
        slug: "livestream",
        title: "Livestream From Your Event",
        blurb: "Full stream production broadcasting your event live to our audience.",
        image: "/packages/livestream.webp", imagePos: "center 30%", /* Dan + display */
      },
    ],
  },
  {
    key: "commissions",
    icon: "🎨",
    title: "Special Orders",
    blurb: "Preserved art, edible masterpieces, and catering — made to order.",
    accent: "var(--jop-pink)",
    tint:   "rgba(239,83,126,0.07)",
    tintSolid: "#1a0a12",
    glow:   "rgba(239,83,126,0.35)",
    items: [
      {
        slug: "preserved-gifts",
        title: "Preserved Pancake Gifts",
        blurb: "A one-of-a-kind pancake artwork, preserved and shipped as a gift. Named, dated, signed. You upload a photo, we paint it, you send it to someone who matters.",
        image: "/packages/preserved-gifts.webp", imagePos: "60% 25%", /* David figure */        // Links to the commissions order flow (purchase + upload) rather
        // than the booking form. Until that flow is built, falls back to /shop.
        customAction: { label: "Order a gift →", href: "/shop" },
      },
      {
        slug: "custom-pancake-box",
        title: "Custom Pancake Box",
        blurb: "A gorgeous custom pancake topper on a warm bed of Wella's poffertjes, boxed and ready for pickup. Birthdays, weddings, milestones — edible art that tastes as good as it looks.",
        image: "/packages/custom-pancake-box.webp", imagePos: "center 35%", /* starry night */
        comingSoon: true,
      },
      {
        slug: "catering",
        title: "Pancake & Poffertjes Catering",
        blurb: "Dozens of custom pancakes and trays of poffertjes prepared and delivered together. Event prep, product launches, office celebrations, media shoots — real food, real art, real volume.",
        image: "/packages/catering.webp", imagePos: "center", /* plate centered */
        comingSoon: true,
      },
    ],
  },
  {
    key: "learning",
    icon: "📚",
    title: "Learning",
    blurb: "Classes and private lessons. We teach, we host, we travel.",
    accent: "var(--jop-highlight-blue)",
    tint:   "rgba(74,164,225,0.07)",
    tintSolid: "#0a1420",
    glow:   "rgba(74,164,225,0.35)",
    items: [
      {
        slug: "private-lessons",
        title: "1-on-1 Private Lessons",
        blurb: "Learn pancake art directly from Dan. In-person in St. Louis or remote via video — real coaching, not a workshop script.",
        image: "/packages/private-lessons.webp", imagePos: "center 20%", /* faces near top */
      },
      {
        slug: "public-classes",
        title: "Public Class Sessions",
        blurb: "Join a scheduled class with other aspiring pancake artists. Beginner through advanced — you leave with your own finished work.",
        image: "/packages/public-classes.webp", imagePos: "center 25%", /* faces up top */
      },
      {
        slug: "book-us-teach",
        title: "Book Us to Teach",
        blurb: "Bring Dan in to teach your team, community group, or school. Workshop-style, hands-on, all ages welcome.",
        image: "/packages/book-us-teach.webp", imagePos: "center 40%", /* overhead crowd */      },
    ],
  },
];

// Navigate to the quote page with the offering slug so QuotePage can
// pre-select the matching service checkbox quietly (no banner).
function goToQuote(slug) {
  const qs = slug ? `?service=${encodeURIComponent(slug)}` : "";
  window.location.href = `/quote${qs}`;
}

export default function Packages() {
  const [openKey, setOpenKey] = useState(null);
  const activeCat = CATEGORIES.find((c) => c.key === openKey) || null;

  // Lock body scroll while modal open + allow Esc to close
  useEffect(() => {
    if (!openKey) return;
    const onKey = (e) => { if (e.key === "Escape") setOpenKey(null); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [openKey]);

  return (
    <section
      id="packages"
      style={{
        background: "radial-gradient(ellipse 60% 50% at 20% 10%, rgba(239,83,126,0.13) 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 80% 90%, rgba(74,164,225,0.13) 0%, transparent 60%), #0e2e46",
        padding: "5rem 1.25rem",
      }}
    >
      <div style={{ maxWidth: "1080px", margin: "0 auto" }}>
        <PanBar width={96} height={4} margin="0 auto 1.1rem" />
        <h2 style={{
          margin: "0 0 0.5rem",
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(2rem, 5vw, 3.2rem)",
          color: "var(--jop-gold)",
          fontWeight: 700,
          letterSpacing: "-0.01em",
          textAlign: "center",
        }}>
          What you can book.
        </h2>
        <p style={{
          margin: "0 auto 2.5rem",
          maxWidth: "520px",
          textAlign: "center",
          color: "rgba(255,218,173,0.65)",
          fontFamily: "var(--jop-display)",
          fontSize: "0.98rem",
          lineHeight: 1.55,
        }}>
          Three ways to work with us. Tap one to see what's inside.
        </p>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1.2rem",
        }}>
          {CATEGORIES.map((c) => (
            <CategoryCard key={c.key} cat={c} onOpen={() => setOpenKey(c.key)} />
          ))}
        </div>
      </div>

      {activeCat && <CategoryModal cat={activeCat} onClose={() => setOpenKey(null)} />}
    </section>
  );
}

function CategoryCard({ cat, onOpen }) {
  return (
    <button
      onClick={onOpen}
      style={{
        background: `linear-gradient(180deg, ${cat.tint} 0%, rgba(255,255,255,0.03) 40%)`,
        border: `1px solid ${cat.glow}`,
        borderTop: `4px solid ${cat.accent}`,
        borderRadius: "14px",
        padding: "1.7rem 1.4rem 1.6rem",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "var(--jop-display)",
        color: "inherit",
        display: "flex",
        flexDirection: "column",
        gap: "0.55rem",
        boxShadow: `0 10px 32px rgba(0,0,0,0.25), 0 0 18px ${cat.glow}`,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = `0 16px 40px rgba(0,0,0,0.3), 0 0 28px ${cat.glow}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = `0 10px 32px rgba(0,0,0,0.25), 0 0 18px ${cat.glow}`;
      }}
    >
      <div style={{ fontSize: "2.2rem", lineHeight: 1 }}>{cat.icon}</div>
      <h3 style={{
        margin: 0,
        fontFamily: "var(--jop-display)",
        fontSize: "1.45rem",
        fontWeight: 800,
        color: cat.accent,
        letterSpacing: "-0.01em",
        textShadow: `0 0 18px ${cat.glow}`,
      }}>
        {cat.title}
      </h3>
      <p style={{
        margin: 0,
        color: "var(--jop-cream)",
        fontSize: "0.95rem",
        lineHeight: 1.5,
        flex: 1,
      }}>
        {cat.blurb}
      </p>
      <span style={{
        marginTop: "0.7rem",
        alignSelf: "flex-start",
        background: cat.tint,
        color: cat.accent,
        fontWeight: 800,
        fontSize: "0.85rem",
        letterSpacing: "0.03em",
        padding: "0.45rem 0.9rem",
        borderRadius: "999px",
        border: `1px solid ${cat.glow}`,
      }}>
        See options →
      </span>
    </button>
  );
}

function CategoryModal({ cat, onClose }) {
  return (
    <>
      {/* Shared CSS — responsive modal positioning + open animation + a
          tasteful thin cream-tinted scrollbar. Replaces the chunky default
          browser scrollbar that was reading as visual noise on the right
          edge of the modal. Uses both ::-webkit-scrollbar (Chromium/Safari)
          and the newer scrollbar-* properties (Firefox) so all browsers
          get a consistent subtle track. */}
      <style>{`
        .jop-modal-backdrop {
          position: fixed; inset: 0;
          background: rgba(6,12,25,0.78);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          z-index: 200;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          animation: jopFadeIn 0.22s ease;
        }
        .jop-modal-panel {
          background: #0b1d34;
          border-top: 4px solid var(--jop-accent);
          box-shadow: 0 -20px 60px rgba(0,0,0,0.55), 0 0 28px var(--jop-glow);
          width: 100%;
          max-width: 640px;
          max-height: 92vh;
          overflow-y: auto;
          border-radius: 18px 18px 0 0;
          animation: jopSlideUp 0.28s cubic-bezier(0.2, 0.8, 0.25, 1);
          scrollbar-width: thin;
          scrollbar-color: rgba(255,218,173,0.22) transparent;
        }
        .jop-modal-panel::-webkit-scrollbar {
          width: 8px;
        }
        .jop-modal-panel::-webkit-scrollbar-track {
          background: transparent;
        }
        .jop-modal-panel::-webkit-scrollbar-thumb {
          background: rgba(255,218,173,0.22);
          border-radius: 8px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        .jop-modal-panel::-webkit-scrollbar-thumb:hover {
          background: rgba(255,218,173,0.38);
          background-clip: padding-box;
          border: 2px solid transparent;
        }
        @media (min-width: 720px) {
          .jop-modal-backdrop { align-items: center; }
          .jop-modal-panel {
            border-radius: 18px;
            border: 1px solid var(--jop-glow);
            border-top-width: 4px;
            animation: jopModalIn 0.22s cubic-bezier(0.2, 0.8, 0.25, 1);
          }
        }
        @keyframes jopFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes jopSlideUp { from { transform: translateY(18px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes jopModalIn { from { transform: scale(0.97); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>

      <div
        className="jop-modal-backdrop"
        onClick={onClose}
      >
        <div
          className="jop-modal-panel"
          onClick={(e) => e.stopPropagation()}
          style={{
            "--jop-accent": cat.accent,
            "--jop-glow":   cat.glow,
          }}
        >
          {/* Header */}
          <div style={{
            position: "sticky", top: 0,
            background: "linear-gradient(180deg, #0b1d34 0%, rgba(11,29,52,0.94) 100%)",
            padding: "1.1rem 1.35rem 0.9rem",
            borderBottom: `1px solid ${cat.glow}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            zIndex: 2,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
              <span style={{ fontSize: "1.6rem", lineHeight: 1 }}>{cat.icon}</span>
              <h3 style={{
                margin: 0,
                color: cat.accent,
                fontFamily: "var(--jop-display)",
                fontSize: "1.3rem",
                fontWeight: 800,
                letterSpacing: "-0.01em",
              }}>
                {cat.title}
              </h3>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,218,173,0.25)",
                color: "rgba(255,218,173,0.75)",
                width: "34px", height: "34px",
                borderRadius: "999px",
                fontSize: "1.1rem",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: "1.1rem 1.35rem 1.6rem" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.95rem" }}>
              {cat.items.map((item) => <OfferingRow key={item.slug} item={item} accent={cat.accent} glow={cat.glow} tint={cat.tint} tintSolid={cat.tintSolid} />)}
            </div>

            {cat.addOns && cat.addOns.length > 0 && (
              <>
                <div style={{
                  margin: "2rem 0 1rem",
                  display: "flex", alignItems: "center", gap: "0.6rem",
                  color: "rgba(255,218,173,0.55)",
                  fontSize: "0.72rem",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}>
                  <span style={{ flex: 1, height: "1px", background: "rgba(255,218,173,0.15)" }} />
                  Add-ons
                  <span style={{ flex: 1, height: "1px", background: "rgba(255,218,173,0.15)" }} />
                </div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  gap: "0.6rem",
                }}>
                  {cat.addOns.map((item) => <AddOnCard key={item.slug} item={item} accent={cat.accent} glow={cat.glow} tint={cat.tint} />)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Main offering row — photo fills the RIGHT side, feathering/fading toward
// the left so copy stays readable. Without a photo, falls back to the
// category-tinted card.
function OfferingRow({ item, accent, glow, tint, tintSolid }) {
  const hasImage = Boolean(item.image);
  return (
    <div style={{
      position: "relative",
      background: hasImage ? tintSolid : tint,
      border: `1px solid ${glow}`,
      borderRadius: "14px",
      overflow: "hidden",
      minHeight: hasImage ? "140px" : undefined,
      opacity: item.comingSoon ? 0.75 : 1,
    }}>
      {hasImage && (
        <div aria-hidden="true" style={{
          position: "absolute", top: 0, right: 0, bottom: 0, left: "38%",
          backgroundImage: `url("${item.image}")`,
          backgroundSize: "cover",
          backgroundPosition: item.imagePos || "center",
          backgroundRepeat: "no-repeat",
          WebkitMaskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.45) 18%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,1) 100%)",
          maskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.45) 18%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,1) 100%)",
          pointerEvents: "none",
        }} />
      )}

      <div style={{
        position: "relative",
        padding: "0.9rem 1.1rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.35rem",
        maxWidth: hasImage ? "58%" : "100%",
      }}>
        <div style={{
          fontFamily: "var(--jop-display)",
          color: accent,
          fontWeight: 800,
          fontSize: "1.05rem",
          letterSpacing: "-0.005em",
          display: "flex", alignItems: "center", gap: "0.45rem", flexWrap: "wrap",
        }}>
          {item.title}
          {item.comingSoon && (
            <span style={{
              fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.08em",
              textTransform: "uppercase", color: "rgba(255,218,173,0.5)",
              background: "rgba(255,218,173,0.06)", border: "1px solid rgba(255,218,173,0.12)",
              borderRadius: "999px", padding: "0.12rem 0.45rem",
            }}>Coming soon</span>
          )}
        </div>
        <p style={{
          margin: 0,
          color: "rgba(255,218,173,0.85)",
          fontFamily: "var(--jop-display)",
          fontSize: "0.84rem",
          lineHeight: 1.5,
        }}>
          {item.blurb}
        </p>

        {item.comingSoon ? (
          <div style={{
            marginTop: "0.15rem",
            color: "rgba(255,218,173,0.4)",
            fontFamily: "var(--jop-display)", fontSize: "0.75rem",
            fontWeight: 600, fontStyle: "italic",
          }}>
            Coming soon — mention it in your inquiry if interested.
          </div>
        ) : item.customAction ? (
          <a
            href={item.customAction.href}
            style={{
              alignSelf: "flex-start", marginTop: "0.2rem",
              background: accent, color: "#1a0a12",
              border: "none", borderRadius: "999px",
              padding: "0.35rem 0.9rem",
              fontFamily: "var(--jop-display)", fontSize: "0.78rem",
              fontWeight: 800, cursor: "pointer",
              letterSpacing: "0.02em", textDecoration: "none",
              boxShadow: `0 4px 14px ${glow}`,
            }}
          >
            {item.customAction.label}
          </a>
        ) : (
          <button
            onClick={() => goToQuote(item.slug)}
            style={{
              alignSelf: "flex-start", marginTop: "0.2rem",
              background: "transparent", color: accent,
              border: `1px solid ${glow}`, borderRadius: "999px",
              padding: "0.3rem 0.8rem",
              fontFamily: "var(--jop-display)", fontSize: "0.78rem",
              fontWeight: 700, cursor: "pointer", letterSpacing: "0.02em",
            }}
          >
            Get a quote →
          </button>
        )}
      </div>
    </div>
  );
}


// Compact add-on card — 2-column grid layout. Smaller, tighter, with a
// subtle background image if available.
function AddOnCard({ item, accent, glow, tint }) {
  const hasImage = Boolean(item.image);
  return (
    <div style={{
      position: "relative",
      background: tint,
      border: `1px solid ${glow}`,
      borderRadius: "10px",
      overflow: "hidden",
      minHeight: hasImage ? "100px" : undefined,
    }}>
      {hasImage && (
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0,
          backgroundImage: `url("${item.image}")`,
          backgroundSize: "cover", backgroundPosition: "center",
          opacity: 0.18, pointerEvents: "none",
        }} />
      )}
      <div style={{
        position: "relative",
        padding: "0.8rem 0.9rem",
        display: "flex", flexDirection: "column", gap: "0.3rem",
      }}>
        <div style={{
          fontFamily: "var(--jop-display)", color: accent,
          fontWeight: 800, fontSize: "0.88rem",
        }}>
          {item.title}
        </div>
        <p style={{
          margin: 0, color: "rgba(255,218,173,0.8)",
          fontFamily: "var(--jop-display)", fontSize: "0.78rem", lineHeight: 1.45,
        }}>
          {item.blurb}
        </p>
        <button
          onClick={() => goToQuote(item.slug)}
          style={{
            alignSelf: "flex-start", marginTop: "0.2rem",
            background: "transparent", color: accent,
            border: `1px solid ${glow}`, borderRadius: "999px",
            padding: "0.25rem 0.65rem", fontFamily: "var(--jop-display)",
            fontSize: "0.72rem", fontWeight: 700, cursor: "pointer",
          }}
        >
          Ask about this →
        </button>
      </div>
    </div>
  );
}

